import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';

export interface CsMenuItem {
    icon: React.ReactNode;
    label: string;
    onClick: () => void;
    /** Renders in red tones (e.g. Remove). */
    destructive?: boolean;
    /** Inserts a divider above this item. */
    dividerBefore?: boolean;
}

interface CanvasContextMenuProps {
    /** Viewport-relative click position. null = closed. */
    position: { x: number; y: number } | null;
    onClose: () => void;
    items: CsMenuItem[];
    /** Optional minimum width override (default 200). */
    minWidth?: number;
}

/**
 * Context menu used both at canvas level (right-click empty background)
 * and at node level (right-click a node). Visual language mirrors the
 * vertical CanvasLeftBar — translucent backdrop-blurred surface, thin
 * border — so every surface feels like the same toolkit seen from
 * different entry points.
 *
 * Hand-positioned rather than built on Radix's ContextMenu: the right-click
 * is caught by the canvas (or by a node) and handed here as coordinates,
 * and Radix's Root has no controlled `open` — it only opens from its own
 * Trigger, which there is nothing here to wrap.
 *
 * Positioning:
 *   - Initial coords = click position.
 *   - After mount, we measure the menu box and clamp it inside the
 *     viewport so it never spills off the edge.
 *
 * Dismissal: click-outside, Escape, or item-selected (via onClose).
 */
export const CanvasContextMenu: React.FC<CanvasContextMenuProps> = ({
    position,
    onClose,
    items,
    minWidth = 200,
}) => {
    const { t } = useTranslation();
    const menuRef = useRef<HTMLDivElement>(null);
    const [clamped, setClamped] = useState<{ x: number; y: number } | null>(null);
    // Index of the destructive item currently armed for confirmation. A first
    // click on a destructive item (e.g. Remove) arms it and keeps the menu open;
    // a second click commits. Reset whenever the menu (re)opens.
    const [confirmIdx, setConfirmIdx] = useState<number | null>(null);
    useEffect(() => { setConfirmIdx(null); }, [position]);

    useEffect(() => {
        if (!position) return;
        const handleClick = (e: MouseEvent) => {
            if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
                onClose();
            }
        };
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        // Defer by a frame so the click that OPENED the menu doesn't
        // immediately close it.
        const raf = requestAnimationFrame(() => {
            document.addEventListener('mousedown', handleClick);
            document.addEventListener('contextmenu', handleClick);
        });
        document.addEventListener('keydown', handleKey);
        return () => {
            cancelAnimationFrame(raf);
            document.removeEventListener('mousedown', handleClick);
            document.removeEventListener('contextmenu', handleClick);
            document.removeEventListener('keydown', handleKey);
        };
    }, [position, onClose]);

    // Clamp within viewport after layout.
    useLayoutEffect(() => {
        if (!position || !menuRef.current) return;
        const rect = menuRef.current.getBoundingClientRect();
        const margin = 8;
        let x = position.x;
        let y = position.y;
        if (x + rect.width + margin > window.innerWidth) {
            x = window.innerWidth - rect.width - margin;
        }
        if (y + rect.height + margin > window.innerHeight) {
            y = window.innerHeight - rect.height - margin;
        }
        if (x < margin) x = margin;
        if (y < margin) y = margin;
        setClamped({ x, y });
    }, [position]);

    if (!position) return null;

    const handleItemClick = (item: CsMenuItem, idx: number) => {
        // Destructive items (Remove / Delete) arm on first click and only
        // commit on the second — mirrors the node "×" confirm so a stray
        // right-click + click never deletes by accident.
        if (item.destructive) {
            if (confirmIdx !== idx) {
                setConfirmIdx(idx);
                return;
            }
        }
        item.onClick();
        onClose();
    };

    const pos = clamped || position;
    const visible = !!clamped;

    // Portal to document.body — any transformed ancestor (e.g. the
    // canvas pan/zoom wrapper) creates a containing block for
    // position:fixed descendants, which makes viewport coords land in
    // the wrong place. The portal escapes that wrapper so clientX/Y
    // land at the cursor regardless of zoom or pan.
    return createPortal(
        <div
            ref={menuRef}
            onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
            onContextMenu={(e: React.MouseEvent) => e.preventDefault()}
            className={cn(
                'fixed z-[1400] flex origin-top-left flex-col gap-0.5 rounded-lg border bg-popover/95 p-1.5 text-popover-foreground backdrop-blur-sm transition-[opacity,transform] duration-100',
                visible ? 'scale-100 opacity-100' : 'scale-[0.98] opacity-0',
            )}
            style={{ left: pos.x, top: pos.y, minWidth }}
        >
            {items.map((item, idx) => {
                const armed = item.destructive && confirmIdx === idx;
                return (
                <React.Fragment key={idx}>
                    {item.dividerBefore && <div className="mx-2 my-0.5 h-px bg-border" />}
                    <button
                        type="button"
                        onClick={() => handleItemClick(item, idx)}
                        className={cn(
                            'flex w-full cursor-pointer items-center gap-3 rounded-lg border border-transparent px-3 py-2 text-start text-sm font-medium transition-colors [&_svg]:size-[18px] [&_svg]:shrink-0',
                            item.destructive
                                ? 'text-destructive hover:bg-destructive/5 hover:text-destructive'
                                : 'text-muted-foreground hover:bg-accent hover:text-foreground',
                            // Armed destructive rows get a red outline + tint to
                            // signal the next click commits (outlined, not filled).
                            armed && 'border-destructive bg-destructive/5',
                        )}
                    >
                        {item.icon}
                        <AnimatePresence mode="wait" initial={false}>
                            <motion.span
                                key={armed ? 'confirm' : 'label'}
                                initial={{ opacity: 0, x: -3 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: 3 }}
                                transition={{ duration: 0.16, ease: 'easeOut' }}
                                style={{ display: 'inline-block' }}
                            >
                                {armed ? t('CONTENT_STUDIO.ACTION_CONFIRM') : item.label}
                            </motion.span>
                        </AnimatePresence>
                    </button>
                </React.Fragment>
                );
            })}
        </div>,
        document.body,
    );
};
